'use client';

import { useEffect, useRef } from 'react';
import { sendGTMEvent } from '../sendGTMEvent';
import { useRouter } from 'next/router';
import { useSessionStorage } from 'react-use';
import { useGTMQueue } from '../useGTMQueue';

type Props = {
  pageType?: string;
  pageTitle?: string;
};

/**
 * Handles tracking of virtual pageview event
 *
 * Expected to be rendered in the root of the application to track pageviews
 */

export const VirtualPageviewTracker = ({ pageType, pageTitle }: Props) => {
  const router = useRouter();
  const pathname = router.asPath;
  const prevPathnameRef = useRef<string | null>(null);
  const [originalLocation, setOriginalLocation] = useSessionStorage<string>('originalLocation');
  const [previousUrl, setPreviousUrl] = useSessionStorage<string>('previousUrl');
  const { addToQueue } = useGTMQueue();

  useEffect(() => {
    const shouldNotTrack = pathname === prevPathnameRef.current;
    if (shouldNotTrack) {
      return;
    }

    if (!originalLocation) {
      setOriginalLocation(location.href);
    }

    // Set previous URL before updating
    const currentUrl = location.href;

    addToQueue((injectedData) => {
      // Get user information from injected data - only if authenticated and available
      const userInfo = injectedData.isAuthenticated ? {
        user_type: 'registered', // Could be enhanced with actual user type from context
        // Additional user fields would need to come from user context when available,
        ...injectedData.user
      } : undefined;

      const eventData: any = {
        event: 'virtual_pageview',
        page_url: currentUrl,
        page_path: `${router.locale}${pathname}`,
        page_title: pageTitle || document.title,
        original_location: originalLocation || currentUrl,
        environment: process.env.NODE_ENV === 'production' ? 'prod' : 'dev',
        login_status: injectedData.isAuthenticated ? 'authenticated' : 'anonymous',
        user_data: injectedData.user || null,
      };

      // Only add optional fields if they have actual values
      if (pageType) {
        eventData.page_type = pageType;
      }
      if (previousUrl) {
        eventData.previousUrl = previousUrl;
      }
      if (injectedData.isAuthenticated && injectedData.basket?.customerId) {
        eventData.user_id = injectedData.basket.customerId;
      }
      if (userInfo) {
        eventData.user_info = userInfo;
      }

      sendGTMEvent(eventData);
    });

    // Update previous URL for next navigation
    setPreviousUrl(currentUrl);

    prevPathnameRef.current = pathname;
  }, [pathname]);

  return null;
};
